import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import { Stack, Spinner, SpinnerSize, MessageBar, MessageBarType } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { HelpPopup } from '@bingads-webui-react/help-popup';

import { EstimationMeter } from './estimation-meter';
import { getReachEstimateStyles } from './styles/reach-estimate-styles';
import { unitValueMap } from '../../../shared/helper/scale-unit';
import { ReachEstimationBreakdown } from './reach-estimation-breakdown';
import { formatBigDecimal } from '../../../shared/helper/format-big-decimal';

const titleReach = _TL_('Potential available reach');
const titleImpression = _TL_('Potential available impression');
const tooNarrowFlag = 'ToNarrow';

const getEstValsAndText = (est) => {
  if (!est) {
    return ['-', 0];
  }
  const estImpressionsHighVal = est.High.Value;
  const estImpressionsHighUnit = unitValueMap[est.High.Unit].unit;
  const estImpressionsHighUnitVal = unitValueMap[est.High.Unit].value;
  const estImpressionsHigh = estImpressionsHighVal * estImpressionsHighUnitVal;

  const estImpressionsLowVal = est.Low.Value;
  const estImpressionsLowUnit = unitValueMap[est.Low.Unit].unit;
  const estImpressionsLowUnitVal = unitValueMap[est.Low.Unit].value;
  const estImpressionsLow = estImpressionsLowVal * estImpressionsLowUnitVal;

  const estImpressionsText = `${estImpressionsLowVal}${estImpressionsLowUnit} - ${estImpressionsHighVal}${estImpressionsHighUnit}`;

  return [estImpressionsText, estImpressionsLow, estImpressionsHigh];
};

const ReachEstimate = (props) => {
  const {
    i18n,
    estimationData,
    isLoading,
    pricingModel,
    isImpressionTooSpecific,
    privacyResult,
    isCampaignCreationPlannerIntegrationEnabled,
    showImpressions,
    hasAudienceRemarketingList,
    updateValidation,
    validationProps,
    showBreakdown,
    isAudienceAdGroupEdit,
    audienceSize,
    ageOnlyEstimation,
    genderOnlyEstimation,
    locationOnlyEstimation,
    audienceOnlyEstimation,
    interestOnlyEstimation,
  } = props;

  const classes = useDefaultStyles(getReachEstimateStyles);
  const activity = useContext(ActivityContext);

  const [showPanel] = useState(true);

  const [estimatedReach, setEstimatedReach] = useState();
  const [estimatedReachLow, setEstimatedReachLow] = useState();
  const [estimatedReachText, setEstimatedReachText] = useState();
  const [estimatedImpression, setEstimatedImpression] = useState();
  const [estimatedImpressionLow, setEstimatedImpressionLow] = useState();
  const [estimatedImpressionText, setEstimatedImpressionText] = useState();

  const [errorMessage, setErrorMessage] = useState(null);

  const updateEstimatesOnMeter = useCallback(() => {
    const [estImpressionText, estImpressionLow, estImpressionHigh] = getEstValsAndText(estimationData && estimationData.EstImpression);

    if (showBreakdown && !isAudienceAdGroupEdit) {
      const estimationAudienceUnit = _.get(audienceSize, ['EstAudienceSize', 'Unit']) || 'None';
      const estimation = unitValueMap[estimationAudienceUnit].value * (_.get(audienceSize, ['EstAudienceSize', 'Value']) || 0);
      const estimationText = formatBigDecimal(estimation, i18n);
      setEstimatedReach(estimation);
      setEstimatedReachText(estimationText);
      setEstimatedReachLow(estimation);
    } else {
      const [estReachText, estReachLow, estReachHigh] = getEstValsAndText(estimationData && estimationData.EstAudienceSize);
      setEstimatedReach(estReachHigh);
      setEstimatedReachText(estReachText);
      setEstimatedReachLow(estReachLow);
    }
    setEstimatedImpression(estImpressionHigh);
    setEstimatedImpressionText(estImpressionText);
    setEstimatedImpressionLow(estImpressionLow);
  }, [estimationData, showBreakdown, isAudienceAdGroupEdit, audienceSize, i18n]);

  useEffect(() => {
    if (!isLoading) {
      updateEstimatesOnMeter(estimationData);

      let errMessage;
      if (privacyResult === 'Pending') {
        errMessage = i18n.getString(_TL_('No estimates available'));
      } else if (privacyResult === 'TooNarrow' && hasAudienceRemarketingList) {
        errMessage = i18n.getString(_TL_('Based on your current remarketing list(s), your targeting is too narrow. Your ads will not run until you expand your remarketing list(s) and increase estimated unique users.'));
      } else if (privacyResult === 'TooNarrow' && hasAudienceRemarketingList) {
        errMessage = i18n.getString(_TL_('The ad group cannot be saved until the targeted audience is more broad.'));
      } else if (isImpressionTooSpecific) {
        errMessage = i18n.getString(_TL_('Very narrow.'));
      }

      if (errMessage) {
        setErrorMessage(errMessage);
        updateValidation([errMessage], { [validationProps.field]: tooNarrowFlag }, false);
      } else {
        setErrorMessage('');
        updateValidation([], { [validationProps.field]: null }, false);
      }

      if (activity) {
        activity.signal(`onReachEstimationChange - ${JSON.stringify({ estimation: estimationData })}`);
      }
    }
  }, [activity, isLoading, estimationData, updateEstimatesOnMeter, i18n, isImpressionTooSpecific, privacyResult, hasAudienceRemarketingList, updateValidation, validationProps]);

  // Allow Updating Estimates no more than once every 2000 ms
  // to-do

  return (
    <div>
      <div className={classNames(classes.reachEstimateControl, 'reach-estimate-control')}>
        {showPanel &&
          <div className={classNames(classes.panel, 'reach-estimate-panel')}>
            <Stack className={classes.panelBody}>
              {isLoading ? (
                <>
                  <div className={classes.reachEstimateTitle}>{i18n.getString(titleReach)}</div>
                  <Stack verticalAlign="center" styles={{ root: { height: 99 } }} className="reach-estimation-loading" >
                    <Spinner size={SpinnerSize.large} label={i18n.getString(_TL_('Loading'))} ariaLive="assertive" labelPosition="right" />
                  </Stack>
                </>
                )
                : (
                  <>
                    <Stack horizontal tokens={{ childrenGap: 5 }} className={classes.impressionsCountMessage}>
                      <div>{i18n.getString(`${titleReach}`)}</div>
                      <HelpPopup
                        topic="pop_BA_Audiences_PotentialReach"
                        aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: titleReach })}
                      />
                    </Stack>
                    <Stack>
                      <div className={classes.estimateNote}>
                        {i18n.getString(_TL_('These estimates are based on your targeting settings and audiences.'))}
                      </div>
                    </Stack>
                    <Stack tokens={{ childrenGap: 4 }}>
                      <div className={classes.estimateHelpContent}>
                        {i18n.getString(_TL_('Potential reach'))}
                        <HelpPopup
                          topicKey={`pop_BA_CampaignCreate_AudAds_Est${pricingModel}`}
                          aria-label={i18n.getString(_TL_('Click to learn more about Potential reach'), { item: 'Potential reach' })}
                        />
                      </div>
                      <EstimationMeter low={estimatedReachLow} high={estimatedReach} />
                      <div className={classNames(classes.impressionsCount, 'impressions-count')}>{estimatedReachText}</div>
                      {errorMessage &&
                      <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline
                      >
                        {errorMessage}
                      </MessageBar>
                      }
                    </Stack>
                    {showBreakdown && !errorMessage && <ReachEstimationBreakdown
                      audienceSize={audienceSize}
                      ageOnlyEstimation={ageOnlyEstimation}
                      genderOnlyEstimation={genderOnlyEstimation}
                      locationOnlyEstimation={locationOnlyEstimation}
                      audienceOnlyEstimation={audienceOnlyEstimation}
                      interestOnlyEstimation={interestOnlyEstimation}
                    />}
                  </>
                )
              }
            </Stack>
          </div>
        }
      </div>
      {isCampaignCreationPlannerIntegrationEnabled && showImpressions &&
        <div className={classNames(classes.reachEstimateControl, 'potential-estimate-control')}>
          {showPanel &&
            <div className={classNames(classes.panel, 'potential-estimate-panel')}>
              <Stack className={classes.panelBody}>
                {isLoading ? (
                  <>
                    <div className={classes.reachEstimateTitle}>{i18n.getString(titleImpression)}</div>
                    <Stack verticalAlign="center" styles={{ root: { height: 99 } }} className="reach-estimation-loading" >
                      <Spinner size={SpinnerSize.large} label={i18n.getString(_TL_('Loading'))} ariaLive="assertive" labelPosition="right" />
                    </Stack>
                  </>
                  )
                  : (
                    <>
                      <Stack horizontal tokens={{ childrenGap: 5 }} className={classes.impressionsCountMessage}>
                        <div>{i18n.getString(`${titleImpression}`)}</div>
                      </Stack>
                      <Stack>
                        <div className={classes.estimateNote}>
                          {i18n.getString(_TL_('These are estimated based on targeting preferences and audience.'))}
                        </div>
                      </Stack>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <div className={classes.estimateHelpContent}>
                          {i18n.getString(_TL_('Potential impression'))}
                          <HelpPopup
                            topicKey={`pop_BA_CampaignCreate_AudAds_Est${pricingModel}`}
                            aria-label={i18n.getString(_TL_('Click to learn more about Potential reach'), { item: 'Potential reach' })}
                          />
                        </div>
                        <EstimationMeter low={estimatedImpressionLow} high={estimatedImpression} />
                        <div className={classNames(classes.impressionsCount, 'impressions-count')}>{estimatedImpressionText}</div>
                        {errorMessage &&
                        <MessageBar
                          messageBarType={MessageBarType.warning}
                          isMultiline
                        >
                          {errorMessage}
                        </MessageBar>
                      }
                      </Stack>
                    </>
                  )
                }
              </Stack>
            </div>
          }
        </div>
  }
    </div>
  );
};

ReachEstimate.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  estimationData: PropTypes.shape({
    EstAudienceSize: PropTypes.number.isRequired,
    EstImpression: PropTypes.number.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  pricingModel: PropTypes.string.isRequired,
  isImpressionTooSpecific: PropTypes.bool.isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    field: PropTypes.string.isRequired,
  }).isRequired,
  isCampaignCreationPlannerIntegrationEnabled: PropTypes.bool,
  showImpressions: PropTypes.bool,
  privacyResult: PropTypes.string.isRequired,
  hasAudienceRemarketingList: PropTypes.bool.isRequired,
  showBreakdown: PropTypes.bool,
  isAudienceAdGroupEdit: PropTypes.bool.isRequired,
  audienceSize: PropTypes.shape({
    EstAudienceSize: PropTypes.shape({
      Value: PropTypes.number,
      Unit: PropTypes.string,
    }),
  }),
  ageOnlyEstimation: PropTypes.shape({}),
  genderOnlyEstimation: PropTypes.shape({}),
  locationOnlyEstimation: PropTypes.shape({}),
  audienceOnlyEstimation: PropTypes.shape({}),
  interestOnlyEstimation: PropTypes.shape({}),
};

ReachEstimate.defaultProps = {
  isCampaignCreationPlannerIntegrationEnabled: false,
  showImpressions: true,
  audienceSize: null,
  ageOnlyEstimation: null,
  genderOnlyEstimation: null,
  locationOnlyEstimation: null,
  audienceOnlyEstimation: null,
  interestOnlyEstimation: null,
  showBreakdown: false,
};

const validatedReachEstimate = withCustomValidation(ReachEstimate);

export { validatedReachEstimate as ReachEstimate };
